/**
 * AttestationViewer — top-level feature: filter bar + timeline + diff.
 *
 * The "Export PDF" button produces a plain-text dump labeled as PDF
 * (mock). When the real backend is wired, swap the implementation
 * for a server-rendered PDF without changing the UI.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonCard } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import type { AttestationRecordFull, EntityId } from '@/api/types';
import { useAttestationList, type AttestationFilters } from '@/hooks/useAttestations';
import { downloadBytes } from '@/utils/download';

import { DiffPanel } from './DiffPanel';
import { FilterBar } from './FilterBar';
import { Timeline } from './Timeline';

export function AttestationViewer({
  root_id,
  gi_name,
}: {
  root_id: EntityId;
  gi_name: string;
}) {
  const { t } = useTranslation('attestations');
  const [filters, setFilters] = useState<AttestationFilters>({});
  const query = useAttestationList(root_id, filters);
  const [pickA, setPickA] = useState<string | null>(null);
  const [pickB, setPickB] = useState<string | null>(null);

  const entries = useMemo(() => query.data ?? [], [query.data]);
  const a = useMemo(() => entries.find((e) => e.id === pickA) ?? null, [entries, pickA]);
  const b = useMemo(() => entries.find((e) => e.id === pickB) ?? null, [entries, pickB]);

  const onPick = (slot: 'A' | 'B', id: string) => {
    if (slot === 'A') setPickA(pickA === id ? null : id);
    else setPickB(pickB === id ? null : id);
  };

  const exportJson = () => {
    const json = JSON.stringify(entries, null, 2);
    downloadBytes(new TextEncoder().encode(json), `${gi_name}-attestations.json`, 'application/json');
  };
  const exportPdf = () => {
    // Mock "PDF": a plain-text dump labeled with a .pdf extension so
    // downstream flows that only check extension behave correctly.
    // The real backend will replace this with a server-rendered PDF.
    const lines: string[] = [
      `Attestation export — ${gi_name}`,
      `Root ID: ${root_id}`,
      `Records: ${entries.length}`,
      ''.padEnd(60, '─'),
      ...entries.flatMap(formatEntryLines),
    ];
    const body = lines.join('\n');
    downloadBytes(new TextEncoder().encode(body), `${gi_name}-attestations.pdf`, 'application/pdf');
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">
          {t('title')}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={exportJson}
            disabled={entries.length === 0}
          >
            {t('export.json')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={exportPdf}
            disabled={entries.length === 0}
          >
            {t('export.pdf')}
          </Button>
        </div>
      </div>

      <FilterBar filters={filters} onChange={setFilters} />

      {a && b ? (
        <DiffPanel
          a={a}
          b={b}
          onClose={() => {
            setPickA(null);
            setPickB(null);
          }}
        />
      ) : null}

      <div>
        {query.isPending ? (
          <div className="flex flex-col gap-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : query.isError ? (
          <ErrorState
            title={t('empty.title')}
            body={(query.error as Error).message}
            onRetry={() => void query.refetch()}
          />
        ) : entries.length === 0 ? (
          <EmptyState title={t('empty.title')} body={t('empty.body')} />
        ) : (
          <>
            <p className="mb-2 text-xs text-ink-subtle">
              {t('count', { value: entries.length })}
            </p>
            <Timeline entries={entries} pickA={pickA} pickB={pickB} onPick={onPick} />
          </>
        )}
      </div>
    </div>
  );
}

function formatEntryLines(e: AttestationRecordFull): string[] {
  return [
    `· ${e.execution_label} @ ${e.signed_at.iso}`,
    `  input_hash  : ${e.input_hash}`,
    `  output_hash : ${e.output_hash}`,
    `  wasm_hash   : ${e.wasm_hash}`,
    `  engine_sig  : ${e.engine_signature}`,
    `  fuel        : ${e.fuel_consumed}`,
    `  bytes       : in=${e.bytes_in} out=${e.bytes_out}`,
    `  witness     : ${e.witness_status}`,
    '',
  ];
}
